import {customElement, property, query, state} from "lit/decorators.js";
import {RenewableEnergyService} from "./renewableEnergyService";
import {resolve} from "../../container";
import type {Maybe} from "../../bootstrap/common/types";
import {SitePowerNumberRequest} from "./sitePowerNumberRequest";
import {Interval} from "../../common/interval";
import {EopImageSpinner} from "../../page/elements/spinner";
import {schedule} from "../../common/utils/promises";
import {isNumber} from "../../bootstrap/common/numbers";
import {AnimatedNumber, AnimatedNumberFactory} from "../../page/elements/animatedNumber";
import {type RenewableNumberResponse} from "./renewableNumberRequest";
import Styles from "./sitePowerNumber.lit.scss";
import {html, LitElement, type PropertyValues, type TemplateResult} from "lit";


export const SITE_POWER_NUMBER_REFRESH_INTERVAL = 20000;

@customElement("eop-site-power-number")
export class EopSitePowerNumber extends LitElement {
    public static readonly styles = Styles;

    private refreshPromise: Promise<void>;
    private spinner: Element;
    private animatedNumber: Maybe<AnimatedNumber>;

    @query("eop-image-spinner")
    protected fetchSpinner: EopImageSpinner;

    @state()
    private value: string;

    @property({attribute: "akc"})
    private akc: string = "";
    @property({attribute: "unit"})
    private unit: string = "";
    @property({attribute: "precision", type: Number})
    private precision: number = 1;

    public constructor(
        private renewableEnergyService: RenewableEnergyService = resolve(RenewableEnergyService),
        private interval: Interval = resolve(Interval),
        private animatedNumberFactory: AnimatedNumberFactory = resolve(AnimatedNumberFactory)
    ) {
        super();
    }

    public disconnectedCallback(): void {
        super.disconnectedCallback();
        this.interval.cancel(this.refreshPromise);
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        const request = new SitePowerNumberRequest(
            this.akc,
            this.unit,
            this.precision
        );

        void this.fetchSpinner.spinWhile(this.updatePower(request));
        this.refreshPromise = schedule(this.interval.repeat(
            () => this.updatePower(request),
            SITE_POWER_NUMBER_REFRESH_INTERVAL))
            .as("fetch");
    }

    private updatePower(request: SitePowerNumberRequest): Promise<void> {
        return schedule(this.fetch(request))
            .as("update-site-power");
    }

    private async fetch(request: SitePowerNumberRequest): Promise<void> {
        try {
            const response = await this.renewableEnergyService.getPowerForSite(request);
            this.getAnimatedNumber().update(this.numberFromResponse(response));
        } catch {
            this.getAnimatedNumber().update(Number.NaN);
        }
    }

    private numberFromResponse(response: RenewableNumberResponse): number {
        return isNumber(response.value) ? response.value : Number.NaN;
    }

    private getAnimatedNumber(): AnimatedNumber {
        this.animatedNumber ??= this.animatedNumberFactory.builder()
            .withFixedPrecision(this.precision)
            .withNumberGroupSeparator(true)
            .withInvalidationText("N/A")
            .withUpdateCallback(updatedValue => this.value = updatedValue)
            .appliedTo(this);
        return this.animatedNumber;
    }

    public render(): TemplateResult {
        return html`<span class="animated-number">${this.value}</span></span>
                <eop-image-spinner></eop-image-spinner>`;
    }
}